/**
 * How-to demos: a scripted expert rides the place where the player went wrong, from a few seconds before it, on the
 * same bike and in the same control mode, and every input is recorded. The sim is deterministic, so the clip plays
 * back exactly in the game's own world (src/app/session.ts, mode 'demo'): no video, no second renderer.
 */
import { BikeConfig } from '../../sim/config';
import { featureLength, isJump, jumpSpan } from '../../sim/features';
import { encodeInput, InputFrame, INPUT_STRIDE_V2 } from '../../sim/input';
import type { Placement } from '../../sim/level';
import { LevelId } from '../../sim/levels';
import { DRILL_CONFIG } from '../config';
import { runDrill } from '../drill';
import { createDrill } from '../drills';
import { FrameState } from '../frame';
import { configHash } from '../replay';
import { GameWorld } from '../world';
import { quantize } from './clipfile';
import { pristineTerrain } from './pristine';
import { brakeRider, Controller, FIGURE8_STRAIGHT, figureEightLine, lineRider, rampThrottle, trackExpert } from './experts';
import { findFeature, Situation, situationKey } from './situation';

/** Something the overlay points at while the demo passes it. */
export type DemoMarkKind = 'brakeHere' | 'gasLip' | 'land' | 'footOut' | 'standUp' | 'sitDown' | 'gasOut' | 'obstacle';
export interface DemoMark { step: number; kind: DemoMarkKind; x: number; y: number; z: number }

/** The player's control mode: the expert rides with the same flags (automatic foot, assists). */
export interface DemoConditions { beginner: boolean; extra?: Partial<InputFrame> }

export interface DemoClip {
  key: string;
  situation: Situation;
  levelId: LevelId;
  /** The expert starts here at rest. "Try again from here" puts the player here too. */
  at: Placement;
  configHash: string;
  dt: number;
  inputs: number[];
  marks: DemoMark[];
  /** Step where the spot itself is (the feature, the stop line): the camera and slow motion centre on it. */
  focusStep: number;
  /** Stars the expert scored (drills), or 3 when it rode the spot without a fall. */
  stars: number;
  /** Where the bike ended, for the determinism check. */
  end: { x: number; y: number; z: number };
}

export const clipSteps = (c: DemoClip): number => c.inputs.length / INPUT_STRIDE_V2;

/** How far before a spot the expert starts (m), and after it the clip runs on (m). */
const LEAD = { jump: 60, berm: 45, obstacle: 40, open: 45 } as const;
const TAIL = 15;
/** Clips never run longer than this (s of sim time). */
const MAX_CLIP = 40;

/** Worlds are costly to build (terrain): one per level, reused (respawn resets the bike). */
const worlds = new Map<string, GameWorld>();
function worldFor(level: LevelId, cfg: BikeConfig, dt: number): GameWorld {
  const k = `${level}|${dt}`;
  let w = worlds.get(k);
  if (!w) { w = new GameWorld(level, 'default', cfg, dt); worlds.set(k, w); }
  w.cfg = cfg;
  pristineTerrain(w.terrain); // untouched ground for every recording: earlier demos left ruts
  return w;
}

const cache = new Map<string, DemoClip | null>();
export const demoCacheKey = (s: Situation, cfg: BikeConfig, dt: number, c: DemoConditions) =>
  `${situationKey(s)}|${configHash(cfg, dt)}|${c.beginner ? 'b' : 'f'}|${JSON.stringify(c.extra ?? {})}`;

/** The cached demo for a situation, generated on first ask; null when no expert run rides it cleanly. */
export function demoFor(s: Situation, cfg: BikeConfig, c: DemoConditions, dt = 1 / 240): DemoClip | null {
  const k = demoCacheKey(s, cfg, dt, c);
  if (!cache.has(k)) cache.set(k, generateDemo(s, cfg, c, dt));
  return cache.get(k)!;
}

export function clearDemoCache(): void { cache.clear(); }

/** Records the expert for a situation. Returns null when the expert itself fell or scored under 3 stars. */
export function generateDemo(s: Situation, cfg: BikeConfig, c: DemoConditions, dt = 1 / 240): DemoClip | null {
  // A few expert variants, calmest last: a bike that falls at the textbook pace still gets a clean demo.
  for (let v = 0; v < 3; v++) {
    const clip = s.drill ? drillDemo(s, cfg, c, dt, v) : trackDemo(s, cfg, c, dt, v);
    if (clip) return clip;
  }
  return null;
}

const flagged = (ctl: Controller, c: DemoConditions): Controller => {
  const extra: Partial<InputFrame> = { ...(c.beginner ? { autoFoot: true } : {}), ...(c.extra ?? {}) };
  // Quantized to the shipped clip's grid (clipfile.ts): the stored stream is exactly what the expert rode.
  return (f) => quantize({ ...ctl(f), ...extra });
};

/** Watches the expert's inputs and places the overlay's markers. */
class Marker {
  readonly marks: DemoMark[] = [];
  private braking = false;
  private foot = false;
  private standing: boolean | null = null;
  private seen = new Set<DemoMarkKind>();

  add(kind: DemoMarkKind, step: number, f: FrameState, once = true): void {
    if (once && this.seen.has(kind)) return;
    this.seen.add(kind);
    const p = f.bike.position;
    this.marks.push({ step, kind, x: p.x, y: p.y, z: p.z });
  }

  watch(step: number, i: InputFrame, f: FrameState, stance: boolean): void {
    const brake = Math.max(i.brake, i.frontBrake ?? 0, i.rearBrake ?? 0);
    const t = f.bike.telemetry;
    if (!this.braking && brake > 0.2 && t.speed > 3) this.add('brakeHere', step, f, false);
    this.braking = brake > 0.05;
    const foot = f.bike.rider.foot > 0.5;
    if (foot && !this.foot) this.add('footOut', step, f);
    this.foot = foot;
    if (t.landing) this.add('land', step, f, false);
    const standing = i.autoPosture ? f.bike.rider.stand > 0.5 : i.standing;
    if (stance && this.standing !== null && standing !== this.standing) this.add(standing ? 'standUp' : 'sitDown', step, f, false);
    this.standing = standing;
  }
}

function finish(s: Situation, w: GameWorld, cfg: BikeConfig, dt: number, at: Placement, inputs: number[], m: Marker, focusStep: number, stars: number): DemoClip {
  const p = w.bike.position;
  return {
    key: situationKey(s), situation: s, levelId: s.level, at, configHash: configHash(cfg, dt), dt, inputs, marks: m.marks,
    focusStep, stars, end: { x: p.x, y: p.y, z: p.z },
  };
}

function drillDemo(s: Situation, cfg: BikeConfig, c: DemoConditions, dt: number, variant: number): DemoClip | null {
  const id = s.drill!;
  const w = worldFor('ring', cfg, dt);
  const at = w.level.starts[id]!;
  w.respawn(at);
  const drill = createDrill(id);
  const base: Controller = id === 'throttle' ? rampThrottle([0.7, 0.6, 0.5][variant]!, [1, 1.3, 1.6][variant]!)
    : id === 'brake' ? brakeRider({ cfg, decel: [5.5, 5, 4.5][variant]! })
      : lineRider(figureEightLine(), (k) => k < FIGURE8_STRAIGHT, [5, 6, 4.5][variant]!, cfg);
  const ctl = flagged(base, c);
  const inputs: number[] = [];
  const m = new Marker();
  let step = 0, focus = 0;
  const t0 = w.time;
  const run = runDrill(drill, w, (f) => {
    const i = ctl(f);
    encodeInput(i, inputs);
    m.watch(step, i, f, id === 'figure8');
    if (id === 'brake' && focus === 0 && Math.max(i.brake, i.frontBrake ?? 0) > 0.2) focus = step;
    step++;
    return i;
  }, t0 + Math.min(MAX_CLIP + 20, DRILL_CONFIG[id].timeLimit + 5));
  const r = run.result;
  if (!r || r.stars < 3) return null;
  // A second of run-out after the finish, idle, so the stop / the gate is seen.
  for (let k = 0; k < Math.round(1 / dt); k++) {
    const i = flagged(() => ({ throttle: 0, brake: id === 'throttle' ? 0.4 : 0, steer: 0, body: 0, standing: false }), c)(w.frame());
    encodeInput(i, inputs);
    w.step(i);
  }
  if (id === 'throttle') focus = Math.round(0.2 / dt);
  if (id === 'figure8') focus = Math.round(2 / dt);
  return finish(s, w, cfg, dt, at, inputs, m, focus, r.stars);
}

function trackDemo(s: Situation, cfg: BikeConfig, c: DemoConditions, dt: number, variant: number): DemoClip | null {
  const w = worldFor(s.level, cfg, dt);
  const level = w.level, line = level.line;
  if (!line || s.s === undefined) return null;
  const f = s.feature ? findFeature(level, s.feature) : null;
  const sectorIndex = s.feature?.startsWith('sector@') ? Number(s.feature.slice(7)) : -1;
  let from: number, to: number, focusS: number;
  if (f) {
    const lead = isJump(f) ? LEAD.jump : f.kind === 'berm' ? LEAD.berm : LEAD.obstacle;
    from = f.s - lead; to = f.s + featureLength(f) + TAIL; focusS = f.s;
  } else if (sectorIndex >= 0) {
    const n = level.checkpoints.length;
    const prev = level.checkpoints[(sectorIndex - 1 + n) % n]!;
    from = prev.s + 2; to = s.s + 5; if (to < from) to += line.length; focusS = from + 20;
  } else {
    from = s.s - LEAD.open; to = s.s + TAIL + 10; focusS = s.s;
  }
  from = line.wrap(from);
  const p = line.pointAt(from), tg = line.tangentAt(from);
  const at: Placement = { x: p.x, z: p.z, heading: Math.atan2(tg.x, tg.z) };
  w.respawn(at);
  const { bot, controller } = trackExpert(level, cfg, from, [1, 0.9, 0.8][variant]!);
  const ctl = flagged(controller, c);
  const lip = f && isJump(f) ? f.s + jumpSpan(f).lip - 2 : NaN;
  const obstacle = f && !isJump(f) && f.kind !== 'berm' ? f.s - 4 : NaN;
  const inputs: number[] = [];
  const m = new Marker();
  const total = to - from + (to < from ? line.length : 0);
  let focus = -1;
  let frame = w.frame();
  for (let step = 0; step < MAX_CLIP / dt; step++) {
    const i = ctl(frame);
    encodeInput(i, inputs);
    const along = from + bot.distance;
    if (focus < 0 && along >= focusS) focus = step;
    if (along >= lip && !m.marks.some((k) => k.kind === 'gasLip')) m.add('gasLip', step, frame);
    if (along >= obstacle && !m.marks.some((k) => k.kind === 'obstacle')) m.add('obstacle', step, frame);
    m.watch(step, i, frame, false);
    frame = w.step(i);
    if (frame.bike.telemetry.fallen) return null;
    if (bot.distance >= total) break;
  }
  if (bot.distance < total) return null;
  return finish(s, w, cfg, dt, at, inputs, m, Math.max(0, focus), 3);
}

/**
 * The demos every build must be able to ride (tests/game/howto.test.ts): all drills and both Zielonka jumps (the
 * first release). Berms, enduro obstacles and other spots are generated on demand the same way.
 */
export const DEMO_CATALOG: readonly Situation[] = [
  { level: 'ring', drill: 'throttle', cause: 'wheelspin' },
  { level: 'ring', drill: 'brake', cause: 'brakeLate' },
  { level: 'ring', drill: 'figure8', cause: 'cones' },
  { level: 'track', s: 59, feature: 'jump@59', cause: 'cased' },
  { level: 'track', s: 1194, feature: 'jump@1194', cause: 'noseLow' },
];
